import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ProjectDetail } from '@gc/shared';
import { ApiError } from '../api/client.js';
import { obtenerProyecto } from '../api/projects.js';
import { EditorEscenas } from '../features/proyectos/EditorEscenas.js';
import { PanelVideoAvanzado } from '../features/advanced-video/PanelVideoAvanzado.js';
import { PanelCreador } from '../features/virtual-creator/PanelCreador.js';
import { Pestanas } from '../components/Pestanas.js';
import { BarreraErrores } from '../components/BarreraErrores.js';
import { PanelDirector } from '../features/proyectos/PanelDirector.js';
import { PanelMusica } from '../features/proyectos/PanelMusica.js';
import { PanelNarracion } from '../features/proyectos/PanelNarracion.js';
import { PanelProduccion } from '../features/proyectos/PanelProduccion.js';
import { PanelResultado } from '../features/proyectos/PanelResultado.js';
import { PanelTrabajos } from '../features/proyectos/PanelTrabajos.js';

type Carga =
  | { readonly tipo: 'cargando' }
  | { readonly tipo: 'listo'; readonly proyecto: ProjectDetail }
  | { readonly tipo: 'error'; readonly mensaje: string; readonly noEncontrado: boolean };

const ESTADOS: Record<string, string> = {
  DRAFT: 'Borrador',
  GENERATING: 'Generando',
  REVIEW: 'En revisión',
  READY: 'Aprobado',
  RENDERING: 'Renderizando',
  COMPLETED: 'Completado',
  ERROR: 'Error',
};

export function Proyecto(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });

  const recargar = useCallback(async () => {
    if (id === undefined) return;
    try {
      setCarga({ tipo: 'listo', proyecto: await obtenerProyecto(id) });
    } catch (causa) {
      const esApi = causa instanceof ApiError;
      setCarga({
        tipo: 'error',
        mensaje: esApi ? causa.message : 'No se pudo cargar el proyecto.',
        noEncontrado: esApi && causa.code === 'NOT_FOUND',
      });
    }
  }, [id]);

  useEffect(() => {
    setCarga({ tipo: 'cargando' });
    void recargar();
  }, [recargar]);

  if (carga.tipo === 'cargando') return <p role="status">Cargando proyecto…</p>;

  if (carga.tipo === 'error') {
    return (
      <section>
        <h1>{carga.noEncontrado ? 'Proyecto no encontrado' : 'Error'}</h1>
        <p role="alert">{carga.mensaje}</p>
        <p>
          <Link to="/">Volver al panel</Link>
        </p>
      </section>
    );
  }

  const { proyecto } = carga;
  const avanzado = proyecto.type === 'ADVANCED_VIDEO' || proyecto.type === 'VIRTUAL_CREATOR';
  const firma = proyecto.scenes.map((e) => `${e.id}:${e.visualAssetId ?? ''}:${e.voiceAssetId ?? ''}`).join('|') + `|${proyecto.settings.musicAssetId ?? ''}`;
  const duracionTotal = proyecto.scenes.reduce((total, escena) => total + escena.durationMs, 0);

  return (
    <section>
      <p>
        <Link to="/">← Panel</Link>
      </p>
      <h1>{proyecto.name}</h1>

      <Pestanas
        etiqueta="Secciones del proyecto"
        inicial={proyecto.status === 'READY' || proyecto.status === 'RENDERING' || proyecto.status === 'COMPLETED' ? 'resultado' : 'produccion'}
        pestanas={[
          {
            id: 'resumen',
            titulo: 'Resumen',
            contenido: <BarreraErrores nombre="Resumen">
              <>
                <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1rem' }}>
                  <dt>Estado</dt>
                  <dd>{ESTADOS[proyecto.status] ?? proyecto.status}</dd>
                  <dt>Revisión</dt>
                  <dd>
                    {proyecto.revision}
                    {proyecto.reviewedRevision === proyecto.revision
                      ? ' (aprobada)'
                      : proyecto.reviewedRevision !== null
                        ? ` (aprobada la ${proyecto.reviewedRevision}, hay cambios sin aprobar)`
                        : ' (sin aprobar)'}
                  </dd>
                  <dt>Formato</dt>
                  <dd>
                    {proyecto.settings.aspectRatio} · objetivo {proyecto.settings.targetDurationMs / 1000} s
                  </dd>
                  <dt>Audio</dt>
                  <dd>
                    Música {proyecto.settings.musicEnabled ? 'sí' : 'no'} · narración{' '}
                    {proyecto.settings.voiceEnabled ? 'sí' : 'no'}
                  </dd>
                  <dt>Duración de las escenas</dt>
                  <dd>
                    {duracionTotal / 1000} s en {proyecto.scenes.length} escena(s)
                  </dd>
                </dl>
                <h2>Idea</h2>
                <p style={{ whiteSpace: 'pre-wrap' }}>{proyecto.description}</p>
                {proyecto.settings.style !== '' && (
                  <>
                    <h2>Estilo visual</h2>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{proyecto.settings.style}</p>
                  </>
                )}
              </>
            </BarreraErrores>,
          },
          {
            id: 'produccion',
            titulo: 'Producción',
            insignia: ESTADOS[proyecto.status] ?? proyecto.status,
            contenido: <BarreraErrores nombre="Producción">{avanzado ? (
              <>
                <PanelDirector projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} settings={proyecto.settings} onCambio={() => void recargar()} />
                {proyecto.type === 'VIRTUAL_CREATOR' && (
                  <PanelCreador projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} onCambio={() => void recargar()} />
                )}
                <PanelVideoAvanzado projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} settings={proyecto.settings} onCambio={() => void recargar()} />
                <PanelProduccion projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} reviewedRevision={proyecto.reviewedRevision} settings={proyecto.settings} firma={firma} soloAprobacion onCambio={() => void recargar()} />
              </>
            ) : (
              <>
                <PanelDirector projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} settings={proyecto.settings} onCambio={() => void recargar()} />
                <PanelProduccion projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} reviewedRevision={proyecto.reviewedRevision} settings={proyecto.settings} firma={firma} onCambio={() => void recargar()} />
              </>
            )}</BarreraErrores>,
          },
          {
            id: 'escenas',
            titulo: avanzado ? 'Planos' : 'Escenas',
            insignia: String(proyecto.scenes.length),
            contenido: <BarreraErrores nombre="Escenas">{proyecto.scenes.length === 0 ? (
                <p>
                  {avanzado
                    ? 'Este proyecto todavía no tiene planos. Genera el storyboard en la pestaña Producción.'
                    : 'Este proyecto todavía no tiene escenas. Pulsa «Generar estructura y assets» en la pestaña Producción para crearlas a partir de la idea.'}
                </p>
              ) : (
                <EditorEscenas projectId={proyecto.id} revision={proyecto.revision} scenes={proyecto.scenes} onCambio={() => void recargar()} />
              )}</BarreraErrores>,
          },
          {
            id: 'narracion',
            titulo: 'Narración',
            insignia: proyecto.settings.voiceEnabled
              ? `${proyecto.scenes.filter((e) => e.voiceAssetId !== null).length}/${proyecto.scenes.filter((e) => (e.text ?? '').trim() !== '').length} con audio`
              : 'opcional',
            contenido: <BarreraErrores nombre="Narración"><PanelNarracion projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} settings={proyecto.settings} scenes={proyecto.scenes} onCambio={() => void recargar()} /></BarreraErrores>,
          },
          {
            id: 'musica',
            titulo: 'Música',
            insignia: proyecto.settings.musicEnabled ? (proyecto.settings.musicAssetId ? 'lista' : 'falta pista') : 'sin música',
            contenido: <BarreraErrores nombre="Música"><PanelMusica projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} settings={proyecto.settings} onCambio={() => void recargar()} /></BarreraErrores>,
          },
          {
            id: 'resultado',
            titulo: 'Resultado',
            contenido: <BarreraErrores nombre="Resultado"><PanelResultado projectId={proyecto.id} revision={proyecto.revision} status={proyecto.status} reviewedRevision={proyecto.reviewedRevision} onCambio={() => void recargar()} /></BarreraErrores>,
          },
          {
            id: 'trabajos',
            titulo: 'Trabajos',
            contenido: <BarreraErrores nombre="Trabajos"><PanelTrabajos projectId={proyecto.id} /></BarreraErrores>,
          },
        ]}
      />
    </section>
  );
}
